// Einstellungen › E-Mail (docs/anmeldung/SPEZIFIKATION.md §4, §8.2, §10.4): Postausgang lesen und bedienen, Absender,
// Vorlagen, Einstellungen und Sperrliste des Betriebs – dazu die eigenen E-Mail-Benachrichtigungen im Profil.
//
// Die Arbeit macht der Mail-Kern (`mail.*` aus '@kernel'): Diese Verträge legen nur Recht, Form und Audit fest.
// Sichtbarkeit einer Nachricht: platform.mail.read ODER selbst ausgelöst ODER Leserecht des Bezugs am Standort – den
// Inhalt zusätzlich nur mit allen Rechten aus `confidential` (prüft der Kern im Handler). Verwaltungswege sind dem
// KI-Assistenten vorenthalten (assistant: 'never', §8.4); lesen darf er Postausgang und Versandstand.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import {
  MAIL_ATTEMPT_OUTCOMES,
  MAIL_LIMITS,
  MAIL_REPLY_TO_MODES,
  MAIL_SENDER_PURPOSES,
  MAIL_STATUSES,
  MAIL_SUPPRESSION_REASONS,
  MAIL_TRANSPORTS,
  MAIL_UNDO_SECONDS,
  zMailSummary,
} from '@shared/mail'
import { z } from 'zod'

const zLocation = z.object({ id: zId, code: z.string(), name: z.string() })
const zFieldIssue = z.object({ path: z.string(), message: z.string() })

// ---- Zustand des Versandwegs -------------------------------------------------------------------------------------

export const zMailStatusView = z.object({
  transport: z.enum(MAIL_TRANSPORTS),
  /** „Mailserver (SMTP)“, „In der Fangstelle abgelegt – nicht verschickt“, „Kein Versandweg eingerichtet“ */
  transportLabel: z.string(),
  /** Betrieb mit settings.demo: alles geht in die Fangstelle */
  demo: z.boolean(),
  /** Absender der Installation (MERXO_MAIL_FROM) – null ohne Einrichtung */
  installationFrom: z.string().nullable(),
  allowedDomains: z.array(z.string()),
  counts: z.object({
    queued: z.int().nonnegative(),
    retry: z.int().nonnegative(),
    held: z.int().nonnegative(),
    sending: z.int().nonnegative(),
    failedLast7Days: z.int().nonnegative(),
    sentLast24Hours: z.int().nonnegative(),
  }),
  /** älteste fällige Nachricht (vorgemerkt bzw. neuer Versuch) */
  oldestDueAt: z.date().nullable(),
  /** letzte vom Mailserver angenommene bzw. in der Fangstelle abgelegte Nachricht */
  lastSentAt: z.date().nullable(),
  /** eine Zeile für die Kopfleiste der Seite */
  line: z.string(),
  tone: z.enum(['success', 'warning', 'info', 'danger']),
})

export const mailStatus = defineContract({
  name: 'platform.mail.status',
  title: 'Zustand des E-Mail-Versands',
  description: 'Ob und wie der Betrieb E-Mails verschickt, wie viele warten und wann zuletzt eine angenommen wurde.',
  llm: {
    description:
      'Liefert den Zustand des E-Mail-Versands: Versandweg (Mailserver, Fangstelle, keiner), wartende und gescheiterte ' +
      'E-Mails, letzte erfolgreiche Zustellung. Verwenden für „Gehen unsere Mails raus?“ oder „Warum kam die Rechnung nicht an?“.',
    keywords: ['E-Mail', 'Mailversand', 'Postausgang', 'Mailserver', 'wartet', 'nicht angekommen'],
  },
  input: z.strictObject({}),
  output: zMailStatusView,
  permission: 'platform.mail.read',
  risk: 'read',
})

// ---- Postausgang ---------------------------------------------------------------------------------------------------

export const mailList = defineContract({
  name: 'platform.mail.list',
  title: 'Postausgang anzeigen',
  description: 'Die E-Mails des Betriebs, neueste zuerst – mit Zustand, Empfängern und Bezug; filterbar nach Zustand, Art, Zeitraum und Suchbegriff.',
  llm: {
    description:
      'Listet die E-Mails des Betriebs (neueste zuerst) mit Zustand je Empfänger. Filter: Zustand, Art (Kategorie), Zeitraum ' +
      '(Tage YYYY-MM-DD), Bezug, Suchbegriff in Betreff oder Empfänger. Verwenden für „Welche Rechnungen gingen heute per ' +
      'Mail raus?“ oder „Ist die Mahnung an Gasthof Post zugestellt?“.',
    keywords: ['Postausgang', 'E-Mails', 'versendet', 'zugestellt', 'Mail an'],
  },
  input: z.strictObject({
    status: z.array(z.enum(MAIL_STATUSES)).max(MAIL_STATUSES.length).optional(),
    category: z.string().trim().min(1).max(80).nullish(),
    /** erster Tag (Europe/Berlin), einschließlich */
    from: z.iso.date().nullish(),
    /** letzter Tag (Europe/Berlin), einschließlich */
    to: z.iso.date().nullish(),
    entity: z.strictObject({ type: z.string().min(1).max(60), id: zId }).nullish(),
    search: z.string().trim().max(100).nullish(),
    limit: z.int().min(1).max(200).default(50),
    /** Fortsetzung: `next` der vorigen Seite */
    after: zId.nullish(),
  }),
  output: z.object({
    items: z.array(zMailSummary),
    next: zId.nullable(),
    /** Arten für den Filter: Schlüssel und Bezeichnung */
    categories: z.array(z.object({ key: z.string(), title: z.string() })),
  }),
  permission: 'platform.mail.read',
  risk: 'read',
})

export const zMailDetail = zMailSummary.extend({
  from: z.object({ name: z.string(), address: z.string() }),
  replyTo: z.string().nullable(),
  envelopeFrom: z.string(),
  messageId: z.string(),
  templateRef: z.string(),
  channel: z.string(),
  attempts: z.int().nonnegative(),
  nextAttemptAt: z.date().nullable(),
  expiresAt: z.date().nullable(),
  /** SHA-256 der vollständigen Nachricht (Rohfassung) */
  rawSha256: z.string().nullable(),
  rawSize: z.int().nonnegative().nullable(),
  /** Rohfassung (.eml) in der Dateiablage – nur für Geschäftsbriefe und nur mit Einsicht in den Inhalt */
  rawFileId: zId.nullable(),
  attachments: z.array(
    z.object({
      position: z.int(),
      fileId: zId,
      fileName: z.string(),
      contentType: z.string(),
      sizeBytes: z.int().nonnegative(),
      sha256: z.string(),
      source: z.string().nullable(),
      inline: z.boolean(),
    }),
  ),
  attemptLog: z.array(
    z.object({
      at: z.date(),
      transport: z.string(),
      outcome: z.enum(MAIL_ATTEMPT_OUTCOMES),
      code: z.string().nullable(),
      text: z.string().nullable(),
      durationMs: z.int().nullable(),
      rawSha256: z.string().nullable(),
    }),
  ),
  /** Vorschau (HTML für einen `<iframe sandbox srcdoc>`, Textfassung) – null, wenn gelöscht oder vertraulich */
  preview: z.object({ html: z.string(), text: z.string() }).nullable(),
  /** der Inhalt ist vertraulich (ein Recht aus `confidential` fehlt) */
  contentHidden: z.boolean(),
  erased: z.boolean(),
  /** was die Person mit dieser Nachricht tun darf */
  can: z.object({ cancel: z.boolean(), resend: z.boolean(), markUndeliverable: z.boolean() }),
})

export const mailGet = defineContract({
  name: 'platform.mail.get',
  title: 'E-Mail anzeigen',
  description:
    'Eine E-Mail mit Kopf, Empfängern samt Antwort des Mailservers, Anhängen mit Prüfsumme, allen Zustellversuchen und der Vorschau.',
  llm: {
    description:
      'Liefert eine E-Mail des Postausgangs im Einzelnen: Absender, Empfänger mit Antwort des Mailservers, Anhänge mit ' +
      'Prüfsumme, Zustellversuche. Die Kennung kommt aus platform.mail.list oder platform.mail.forEntity.',
    keywords: ['E-Mail', 'Zustellung', 'Antwort des Mailservers', 'Anhang'],
  },
  input: z.strictObject({ mailId: zId }),
  output: zMailDetail,
  // Sichtbar mit platform.mail.read, für die auslösende Person oder mit dem Leserecht des Bezugs – prüft der Kern
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

export const mailForEntity = defineContract({
  name: 'platform.mail.forEntity',
  title: 'Versandstand eines Datensatzes',
  description: 'Die E-Mails zu einem Beleg oder Datensatz – nur die, die die Person sehen darf.',
  llm: {
    description:
      'Liefert die E-Mails zu einem Datensatz (z. B. einer Rechnung: type „document“ und ihre Kennung) mit Zustand je ' +
      'Empfänger. Verwenden für „Wurde die Rechnung RE-… verschickt?“.',
    keywords: ['verschickt', 'per E-Mail', 'Versandstand', 'zugestellt'],
  },
  input: z.strictObject({ type: z.string().trim().min(1).max(60), id: zId }),
  output: z.object({ items: z.array(zMailSummary) }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

export const mailCancel = defineContract({
  name: 'platform.mail.cancel',
  title: 'E-Mail abbrechen',
  description: 'Hält eine E-Mail vor dem Versand an – nur wer sie ausgelöst hat oder den Postausgang verwaltet.',
  llm: { description: 'Bricht eine E-Mail vor dem Versand ab. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ mailId: zId }),
  output: z.object({ cancelled: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})

export const mailResend = defineContract({
  name: 'platform.mail.resend',
  title: 'E-Mail erneut senden',
  description:
    'Legt eine neue E-Mail mit demselben Inhalt und denselben Empfängern an; die ursprüngliche bleibt unverändert im Protokoll.',
  llm: { description: 'Sendet eine E-Mail erneut. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ mailId: zId }),
  output: z.object({ mailId: zId, status: z.enum(MAIL_STATUSES) }),
  permission: 'platform.mail.manage',
  risk: 'write',
  effects: { external: true },
  assistant: 'never',
})

export const mailMarkUndeliverable = defineContract({
  name: 'platform.mail.markUndeliverable',
  title: 'Empfänger als unzustellbar markieren',
  description:
    'Vermerkt einen Rückläufer, der im normalen Postfach ankam: Die E-Mail zeigt dann „nicht zugestellt“, wahlweise kommt die Adresse auf die Sperrliste.',
  llm: { description: 'Vermerkt einen Rückläufer. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    mailId: zId,
    address: z.string().trim().min(3).max(MAIL_LIMITS.address),
    note: z.string().trim().max(300).nullish(),
    /** Adresse zusätzlich auf die Sperrliste setzen */
    suppress: z.boolean().default(false),
  }),
  output: z.object({ marked: z.literal(true), suppressed: z.boolean() }),
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

// ---- Absender ------------------------------------------------------------------------------------------------------

export const zMailSender = z.object({
  id: zId,
  purpose: z.enum(MAIL_SENDER_PURPOSES),
  purposeLabel: z.string(),
  locationId: zId.nullable(),
  fromName: z.string(),
  fromAddress: z.string(),
  replyTo: z.string().nullable(),
  active: z.boolean(),
  /** die Domäne ist auf diesem Server (noch) freigegeben */
  allowed: z.boolean(),
  version: z.int(),
})

export const mailSenderList = defineContract({
  name: 'platform.mail.sender.list',
  title: 'Absender anzeigen',
  description: 'Absender je Zweck und Standort, dazu die auf diesem Server freigegebenen Domänen und der Absender der Installation.',
  llm: { description: 'Absender des Betriebs. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({
    senders: z.array(zMailSender),
    allowedDomains: z.array(z.string()),
    installation: z.object({ name: z.string(), address: z.string() }),
    locations: z.array(zLocation),
  }),
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

export const mailSenderSave = defineContract({
  name: 'platform.mail.sender.save',
  title: 'Absender speichern',
  description:
    'Legt einen Absender für einen Zweck (und wahlweise einen Standort) an oder ändert ihn. Erlaubt sind nur Domänen, die der Betreiber freigegeben hat.',
  llm: { description: 'Speichert einen Absender. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    id: zId.nullish(),
    purpose: z.enum(MAIL_SENDER_PURPOSES),
    locationId: zId.nullish(),
    fromName: z.string().max(200),
    fromAddress: z.string().max(320),
    replyTo: z.string().max(320).nullish(),
    active: z.boolean().default(true),
    /** Version beim Laden (gleichzeitige Änderung) */
    version: z.int().nullish(),
  }),
  output: zMailSender,
  permission: 'platform.mail.manage',
  risk: 'write',
  // Ein Absender spricht im Namen des Betriebs: frische Bestätigung wie bei Zugängen
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const mailSenderRemove = defineContract({
  name: 'platform.mail.sender.remove',
  title: 'Absender entfernen',
  description: 'Entfernt einen Absender. E-Mails dieses Zwecks gehen danach vom nächsten passenden Absender bzw. dem der Installation.',
  llm: { description: 'Entfernt einen Absender. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ id: zId }),
  output: z.object({ removed: z.literal(true) }),
  permission: 'platform.mail.manage',
  risk: 'destructive',
  effects: { securitySensitive: true },
  assistant: 'never',
})

export const mailSenderTest = defineContract({
  name: 'platform.mail.sender.test',
  title: 'Testmail an mich',
  description: 'Schickt eine Testmail an Ihre eigene Adresse – über den gewählten Absender bzw. den der Installation.',
  llm: { description: 'Schickt eine Testmail. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    /** gespeicherter Absender; ohne Angabe der Absender für Systemmeldungen bzw. der Installation */
    senderId: zId.nullish(),
  }),
  output: z.object({
    mailId: zId,
    status: z.enum(MAIL_STATUSES),
    /** die eigene Adresse */
    to: z.string(),
    from: z.string(),
  }),
  permission: 'platform.mail.manage',
  risk: 'write',
  effects: { external: true },
  assistant: 'never',
})

// ---- Vorlagen ------------------------------------------------------------------------------------------------------

const zTemplateText = z.object({ subject: z.string(), intro: z.string(), closing: z.string().nullable() })

export const zMailTemplateItem = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  module: z.string(),
  /** was der Betrieb ändern darf */
  editable: z.enum(['all', 'intro', 'none']),
  /** Geschäftsbrief (Rohfassung wird aufbewahrt) */
  business: z.boolean(),
  /** abbestellbar */
  optional: z.boolean(),
  /** eigene Texte gespeichert */
  customized: z.boolean(),
  version: z.int().nullable(),
  updatedAt: z.date().nullable(),
})

export const zMailTemplateDetail = zMailTemplateItem.extend({
  defaults: zTemplateText,
  custom: zTemplateText.nullable(),
  /** Platzhalter der Vorlage und des Kerns: Name → Bezeichnung */
  variables: z.record(z.string(), z.string()),
  required: z.array(z.string()),
})

export const mailTemplateList = defineContract({
  name: 'platform.mail.template.list',
  title: 'E-Mail-Vorlagen anzeigen',
  description: 'Die Vorlagen je Art: Rechnung, Mahnung, Bestellung, Einladung … – mit dem Hinweis, ob eigene Texte gelten.',
  llm: { description: 'Vorlagen des Betriebs. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({ templates: z.array(zMailTemplateItem) }),
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

export const mailTemplateGet = defineContract({
  name: 'platform.mail.template.get',
  title: 'E-Mail-Vorlage anzeigen',
  description: 'Eine Vorlage mit mitgelieferten und eigenen Texten und ihren Platzhaltern.',
  llm: { description: 'Eine Vorlage. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ key: z.string().trim().min(3).max(80) }),
  output: zMailTemplateDetail,
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

export const mailTemplateSave = defineContract({
  name: 'platform.mail.template.save',
  title: 'E-Mail-Vorlage speichern',
  description:
    'Speichert Betreff, Einleitung und Schluss einer Vorlage. Erlaubt sind nur die angebotenen Platzhalter; Links setzt das System selbst.',
  llm: { description: 'Speichert eine Vorlage. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    key: z.string().trim().min(3).max(80),
    subject: z.string().max(MAIL_LIMITS.subject + 50).nullish(),
    intro: z.string().max(MAIL_LIMITS.intro + 100),
    closing: z.string().max(MAIL_LIMITS.closing + 100).nullish(),
    version: z.int().nullish(),
  }),
  output: zMailTemplateDetail,
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

export const mailTemplateReset = defineContract({
  name: 'platform.mail.template.reset',
  title: 'E-Mail-Vorlage zurücksetzen',
  description: 'Stellt die mitgelieferten Texte einer Vorlage wieder her; die eigenen Texte bleiben in der Historie.',
  llm: { description: 'Setzt eine Vorlage zurück. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ key: z.string().trim().min(3).max(80) }),
  output: zMailTemplateDetail,
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

export const mailTemplatePreview = defineContract({
  name: 'platform.mail.template.preview',
  title: 'E-Mail-Vorlage in der Vorschau',
  description: 'Zeigt eine Vorlage mit Beispielwerten – auch mit noch nicht gespeicherten Texten.',
  llm: { description: 'Vorschau einer Vorlage. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    key: z.string().trim().min(3).max(80),
    texts: z
      .strictObject({
        subject: z.string().max(MAIL_LIMITS.subject + 50).nullish(),
        intro: z.string().max(MAIL_LIMITS.intro + 100).nullish(),
        closing: z.string().max(MAIL_LIMITS.closing + 100).nullish(),
      })
      .nullish(),
  }),
  output: z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
    /** fehlende Pflichtangaben der Fußzeile („Firmendaten unvollständig“) */
    footerGaps: z.array(z.string()),
    /** Feldfehler der ungespeicherten Texte (die Vorschau zeigt dann die gültigen) */
    issues: z.array(zFieldIssue),
  }),
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

// ---- Einstellungen -------------------------------------------------------------------------------------------------

export const zMailSettings = z.object({
  replyToMode: z.enum(MAIL_REPLY_TO_MODES),
  replyToAddress: z.string().nullable(),
  signature: z.string().nullable(),
  legalFooter: z.boolean(),
  undoSeconds: z.int().min(MAIL_UNDO_SECONDS.min).max(MAIL_UNDO_SECONDS.max),
  version: z.int().nullable(),
})

export const mailSettingsGet = defineContract({
  name: 'platform.mail.settings.get',
  title: 'E-Mail-Einstellungen anzeigen',
  description: 'Antwort-An-Regel, Signatur, Fußzeile mit Pflichtangaben und das Rückgängig-Fenster für Belegmails.',
  llm: { description: 'E-Mail-Einstellungen. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({}),
  output: zMailSettings,
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

export const mailSettingsUpdate = defineContract({
  name: 'platform.mail.settings.update',
  title: 'E-Mail-Einstellungen ändern',
  description: 'Ändert Antwort-An-Regel, Signatur, Fußzeile und Rückgängig-Fenster.',
  llm: { description: 'Ändert die E-Mail-Einstellungen. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    replyToMode: z.enum(MAIL_REPLY_TO_MODES).optional(),
    replyToAddress: z.string().max(320).nullish(),
    signature: z.string().max(MAIL_LIMITS.signature + 100).nullish(),
    legalFooter: z.boolean().optional(),
    undoSeconds: z.int().optional(),
    version: z.int().nullish(),
  }),
  output: zMailSettings,
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

// ---- Sperrliste ----------------------------------------------------------------------------------------------------

export const zMailSuppression = z.object({
  id: zId,
  address: z.string(),
  category: z.string().nullable(),
  categoryTitle: z.string().nullable(),
  reason: z.enum(MAIL_SUPPRESSION_REASONS),
  reasonLabel: z.string(),
  source: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.date(),
  createdByName: z.string().nullable(),
  liftedAt: z.date().nullable(),
  liftedByName: z.string().nullable(),
  liftReason: z.string().nullable(),
})

export const mailSuppressionList = defineContract({
  name: 'platform.mail.suppression.list',
  title: 'Sperrliste anzeigen',
  description: 'Adressen, an die nichts mehr geht – unzustellbar, abbestellt oder von Hand gesperrt.',
  llm: { description: 'Sperrliste des Betriebs. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ includeLifted: z.boolean().default(false), search: z.string().trim().max(100).nullish() }),
  output: z.object({ entries: z.array(zMailSuppression) }),
  permission: 'platform.mail.read',
  risk: 'read',
  assistant: 'never',
})

export const mailSuppressionAdd = defineContract({
  name: 'platform.mail.suppression.add',
  title: 'Adresse sperren',
  description: 'Setzt eine Adresse auf die Sperrliste – für alle E-Mails oder nur für eine Art.',
  llm: { description: 'Sperrt eine Adresse. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({
    address: z.string().trim().min(3).max(320),
    category: z.string().trim().min(3).max(80).nullish(),
    reason: z.enum(['manual', 'invalid']).default('manual'),
    note: z.string().trim().max(1000).nullish(),
  }),
  output: zMailSuppression,
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

export const mailSuppressionLift = defineContract({
  name: 'platform.mail.suppression.lift',
  title: 'Sperre aufheben',
  description: 'Hebt einen Eintrag der Sperrliste mit Grund auf – an die Adresse geht danach wieder Post.',
  llm: { description: 'Hebt eine Sperre auf. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ id: zId, reason: z.string().trim().max(1000) }),
  output: zMailSuppression,
  permission: 'platform.mail.manage',
  risk: 'write',
  assistant: 'never',
})

// ---- Profil: eigene E-Mail-Benachrichtigungen (§5.8, §10.2) -------------------------------------------------------

export const zMailPreference = z.object({
  category: z.string(),
  title: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  /** noch nie eingestellt – es gilt der Standard (an für die Geschäftsführung, sonst aus) */
  isDefault: z.boolean(),
})

export const profileMailPreferences = defineContract({
  name: 'platform.profile.mailPreferences',
  title: 'Eigene E-Mail-Benachrichtigungen anzeigen',
  description: 'Welche Systemmeldungen Sie zusätzlich per E-Mail erhalten.',
  llm: {
    description: 'Welche Benachrichtigungen die angemeldete Person zusätzlich per E-Mail bekommt. Verwenden für „Bekomme ich Warnungen per Mail?“.',
    keywords: ['Benachrichtigung', 'E-Mail', 'abbestellen', 'Warnung per Mail'],
  },
  input: z.strictObject({}),
  output: z.object({ preferences: z.array(zMailPreference), email: z.string() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

export const profileSetMailPreference = defineContract({
  name: 'platform.profile.setMailPreference',
  title: 'E-Mail-Benachrichtigung ein- oder ausschalten',
  description: 'Schaltet eine abbestellbare Art von E-Mails für Sie ein oder aus.',
  llm: { description: 'Schaltet eigene E-Mail-Benachrichtigungen um. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({ category: z.string().trim().min(3).max(80), enabled: z.boolean() }),
  output: z.object({ category: z.string(), enabled: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  // persönlicher Zustand ohne Geschäftsbezug (§8.2)
  audit: 'none',
  assistant: 'never',
})
